import { ArrowDown01Icon, ArrowUp01Icon, Camera01Icon, Cursor01Icon, CursorInfo01Icon, PlayIcon, StopIcon, Video01Icon } from '@hugeicons/core-free-icons';
import { attachmentSizeLabel } from '../commentAttachments';
import { CAPTURE_MODE_COPY, RECORD_COPY, RECORD_LIMITS, formatRecordClock, recordingFileName } from '../constants/captureCopy';
import { commentVisibilityLabel } from '../types/issue-comments';
import { createIcon } from './iconDom';
import { createToggle } from './toggleDom';
import { renderInspectorTooltip } from './inspectorTooltipDom';
/**
 * The illustrations a feature callout shows (featureCalloutDom.ts), one entry per tool, each drawn at true size from
 * the tool's own parts. Every card is the same size (overlayqa-49's contract, 2026-10-26): the callout gives each
 * entry a fixed 360 x 152 scene with two slots. `page` is the white page panel and clips; `over` sits on top, is
 * not clipped, and may hang into the stage's 44px bottom room (a pointer, a toolbar, a file row). Motion lives in
 * featureGuides.css, one timeline per entry in --oq-motion-moment multiples; resting styles are the finished frame,
 * which reduced motion shows.
 *
 * DOM, not React, so the plain-JS guest page draws the same card. A new entry adds its id here and its rules in
 * featureGuides.css; featureCallout.singleSource.source.test.ts refuses illustration markup anywhere else.
 */
export const FEATURE_ILLUSTRATION_IDS = ['spacing', 'recorder', 'inspector', 'findings', 'overlay'];
const SVG_NS = 'http://www.w3.org/2000/svg';
/** The welcome illustration's pointer (onboarding/AuthPreview.tsx), the one the Spacing guide shipped with. */
const CURSOR_PATH = 'M1.5 1L15.5 10.5L9.5 11.5L6.5 19L1.5 1Z';
function part(doc, className, text) {
    const node = doc.createElement('span');
    node.className = className;
    if (text !== undefined)
        node.textContent = text;
    return node;
}
function cursor(doc, className) {
    const svg = doc.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', className);
    svg.setAttribute('viewBox', '0 0 18 22');
    svg.setAttribute('fill', 'none');
    const path = doc.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', CURSOR_PATH);
    path.setAttribute('stroke-width', '1.5');
    path.setAttribute('stroke-linejoin', 'round');
    svg.append(path);
    return svg;
}
/** The Recorder take the loop films: about 4.4 s, so the clock reads 0:04 when Stop recording is pressed. */
const RECORD_TAKE_SECONDS = 4.4;
const RECORD_CLOCKS = [0, 1, 2, 3, 4].map(s => formatRecordClock(s * 1000));
/** The saved comment's file row: the shipped file name, size label and visibility for that take. */
const RECORD_FILE_NAME = recordingFileName(new Date(2026, 9, 26, 9, 42), 'mp4');
const RECORD_FILE_META = `${attachmentSizeLabel(Math.round(RECORD_TAKE_SECONDS * RECORD_LIMITS.bitsPerSecond / 8))} · ${commentVisibilityLabel('internal')}`;
const RECORD = 'oq-feature-guide__record';
/** The page every entry shares: a form field above a Submit button, 24px apart (the Spacing guide's page). */
function formPage(doc, modifier) {
    const stack = part(doc, `oq-feature-guide__stack oq-feature-guide__stack--${modifier}`);
    const field = part(doc, 'oq-feature-guide__field');
    const target = part(doc, 'oq-feature-guide__target', 'Submit');
    stack.append(field, part(doc, 'oq-feature-guide__spacer'), target);
    return { stack, field, target };
}
export const FEATURE_ILLUSTRATIONS = {
    /** Approved inspector switch gesture, from docs/prototypes/element-token-sources/. */
    inspector: {
        label: 'Pointing at an element shows the hover inspector; the arrow beside Create turns it off, and pointing then shows only the outline',
        build(doc, { page, over }) {
            const P = 'oq-feature-guide__inspect';
            const el = (tag, classes, ...kids) => {
                const node = doc.createElement(tag);
                node.className = classes.split(' ').map(c => `${P}-${c}`).join(' ');
                node.append(...kids);
                return node;
            };
            const icon = (glyph, name) => createIcon(doc, glyph, name);
            const tipHost = el('span', 'tip');
            const badge = el('span', 'badge', '+3.1%');
            page.append(el('div', 'page', el('span', 'target', badge, tipHost)));
            const arrow = doc.createElementNS(SVG_NS, 'svg');
            arrow.setAttribute('class', `${P}-arrow`);
            arrow.setAttribute('viewBox', '0 0 18 22');
            arrow.setAttribute('fill', 'none');
            arrow.setAttribute('aria-hidden', 'true');
            const path = doc.createElementNS(SVG_NS, 'path');
            path.setAttribute('d', CURSOR_PATH);
            path.setAttribute('stroke-width', '1.5');
            path.setAttribute('stroke-linejoin', 'round');
            arrow.append(path);
            const toggle = createToggle(doc, { checked: false, label: CAPTURE_MODE_COPY.hoverInspector });
            toggle.classList.add(`${P}-toggle`);
            toggle.tabIndex = -1;
            toggle.setAttribute('aria-hidden', 'true');
            toggle.inert = true;
            over.append(el('div', 'menu', el('span', 'row', icon(CursorInfo01Icon, 'inspector'), el('span', 'label', CAPTURE_MODE_COPY.hoverInspector), toggle)), el('div', 'bar', el('span', 'group', el('span', 'tile tile--create', icon(Cursor01Icon, 'select')), el('span', 'caret', icon(ArrowUp01Icon, 'select-caret'))), el('span', 'tile', icon(Camera01Icon, 'screenshot'))), el('span', 'pointer', el('span', 'click'), arrow));
            // The inspector draws once the badge has laid out, from the badge's own measured box and computed styles, so the
            // tooltip states what the drawing shows (the Spacing guide's rule: the drawn geometry is the measured value).
            let tries = 0;
            const draw = () => {
                if (!badge.isConnected || !badge.offsetWidth) {
                    if (tries++ < 30)
                        requestAnimationFrame(draw);
                    return;
                }
                const cs = getComputedStyle(badge);
                const tip = doc.createElement('div');
                tipHost.append(tip);
                renderInspectorTooltip(tip, {
                    selector: '.trend-badge',
                    dimensions: { width: badge.offsetWidth, height: badge.offsetHeight },
                    display: cs.display, padding: cs.padding, borderRadius: cs.borderRadius,
                });
            };
            requestAnimationFrame(draw);
        },
    },
    /** The inspector measuring Submit with its own marks; the 24px gap and 20px side padding are drawn at true size. */
    spacing: {
        label: 'Spacing shows measurements around a page element',
        build(doc, { page }) {
            const gap = part(doc, 'oq-feature-guide__gap');
            gap.append(part(doc, 'oq-feature-guide__label', '24'));
            const target = part(doc, 'oq-feature-guide__target');
            const band = (side) => {
                const node = part(doc, `oq-feature-guide__band oq-feature-guide__band--${side}`);
                node.append(part(doc, 'oq-feature-guide__label', 'P 20'));
                return node;
            };
            target.append(band('start'), doc.createTextNode('Submit'), band('end'), cursor(doc, 'oq-feature-guide__cursor'));
            const stack = part(doc, 'oq-feature-guide__stack oq-feature-guide__stack--spacing');
            stack.append(part(doc, 'oq-feature-guide__field'), gap, target);
            page.append(stack);
        },
    },
    /**
     * A form on the page panel, filmed with Record's own parts at true size: the area step's dim, two-tone edge and brand
     * handles (review-guest-app/sections/0235-record-tool.js recordDrawBox, recordEdge) and its crosshair
     * (recordCrosshair); the capture toolbar's Record states (captureToolbarDom.ts: the pressed Record tile, "Esc to
     * cancel" with Start recording, then the clock with Stop recording); and the comment's file row (PopoverFiles.tsx
     * FileRow). The recorded interaction is a dropdown: the pointer opens it, picks the second option and presses Submit,
     * and the field turns red. A ring marks each click, because a take is about what the person did, which a screenshot
     * cannot show. Approved by Emily 2026-10-26 (docs/prototypes/feature-guide-recorder/).
     */
    recorder: {
        label: 'Recorder records a chosen area of the page while you open a menu, pick an option and press Submit',
        build(doc, { page, over }) {
            const el = (tag, classes, ...kids) => {
                const node = doc.createElement(tag);
                node.className = classes.split(' ').map(c => `${RECORD}-${c}`).join(' ');
                node.append(...kids);
                return node;
            };
            const icon = (glyph) => createIcon(doc, glyph, 'record');
            const lines = (className, viewBox, paths) => {
                const root = doc.createElementNS(SVG_NS, 'svg');
                root.setAttribute('class', `${RECORD}-${className}`);
                root.setAttribute('viewBox', viewBox);
                root.setAttribute('fill', 'none');
                root.setAttribute('aria-hidden', 'true');
                for (const [d, width] of paths) {
                    const path = doc.createElementNS(SVG_NS, 'path');
                    path.setAttribute('d', d);
                    path.setAttribute('stroke-width', width);
                    path.setAttribute('stroke-linejoin', 'round');
                    root.append(path);
                }
                return root;
            };
            // The page: a select and Submit, the select's menu, the error mark, and the chosen area over all of it.
            const options = [1, 2, 3].map(n => el('span', `option option--${n}`, el('span', 'option-bar')));
            page.append(el('div', 'form', el('div', 'row', el('span', 'select', el('span', 'value'), icon(ArrowDown01Icon), el('span', 'menu', ...options)), el('span', 'submit', 'Submit')), el('span', 'error')), el('span', 'area', el('span', 'handles')));
            // Over the page: the toolbar, the saved file row, and the two pointers.
            const thumb = doc.createElement('span');
            thumb.className = 'oq-popover__file-thumb';
            thumb.append(icon(PlayIcon));
            over.append(el('div', 'bar', el('span', 'tile', icon(Video01Icon)), el('span', 'divider'), el('span', 'states', el('span', 'state state--area', el('span', 'hint', el('kbd', 'kbd', 'Esc'), ` ${RECORD_COPY.areaHintTail}`), el('span', 'primary primary--start', icon(Video01Icon), RECORD_COPY.start)), el('span', 'state state--recording', el('span', 'hint', el('span', 'clock', el('span', 'clock-strip', ...RECORD_CLOCKS.map(c => el('span', 'clock-reading', c)))), RECORD_COPY.timer('', formatRecordClock(RECORD_LIMITS.capMs))), el('span', 'primary primary--stop', icon(StopIcon), RECORD_COPY.stop)))), el('span', 'file', thumb, el('span', 'file-text', el('span', 'file-name', RECORD_FILE_NAME), el('span', 'file-meta', RECORD_FILE_META))), lines('crosshair', '0 0 24 24', [['M12 1v22M1 12h22', '3'], ['M12 1v22M1 12h22', '1']]), el('span', 'pointer', el('span', 'click'), lines('arrow', '0 0 18 22', [[CURSOR_PATH, '1.5']])));
        },
    },
    /** The review canvas's numbered severity markers (FindingMarkers.tsx) landing on the page's elements in turn. */
    findings: {
        label: 'A workflow marks what it finds on the page',
        build(doc, { page }) {
            const { stack, field, target } = formPage(doc, 'findings');
            field.append(part(doc, 'oq-feature-guide__marker oq-feature-guide__marker--1', '1'));
            target.append(part(doc, 'oq-feature-guide__marker oq-feature-guide__marker--2', '2'));
            page.append(stack);
        },
    },
    /** Visual Comparison: the design laid over the page at half strength, a little off where the build is off. */
    overlay: {
        label: 'Visual Comparison lays the design over the page',
        build(doc, { page }) {
            const { stack } = formPage(doc, 'overlay');
            const design = part(doc, 'oq-feature-guide__target oq-feature-guide__design', 'Submit');
            design.setAttribute('aria-hidden', 'true');
            stack.append(design);
            page.append(stack);
        },
    },
};
/** The brand gradient's stops (extension _tokens.css --oq-brand-gradient); the shared palette has no rainbow token yet. */
// ds-hand-rolled: the brand gradient's own stops, mirrored from --oq-brand-gradient until a shared token exists
const STOPS = ['#3468F8', '#6366F1', '#F87171', '#FB923C'];
/**
 * The warped rainbow field behind every illustration: the brand gradient under a turbulence warp, held still. The
 * same artwork as the What's New hero (packages/dashboard/public/whats-new-hero.svg) without its drift. Ids are
 * prefixed per callout so two cards on one page never share a filter.
 */
export function rainbowWarpMarkup(prefix) {
    const rainbow = `${prefix}-rainbow`, warp = `${prefix}-warp`, grain = `${prefix}-grain`;
    const stops = STOPS.map((color, i) => `<stop offset="${[0, 0.38, 0.72, 1][i]}" stop-color="${color}"/>`).join('');
    const bands = [
        'M-80 90 C 160 -10, 300 230, 520 120 S 820 40, 900 160',
        'M-80 180 C 140 90, 320 320, 540 210 S 820 120, 900 260',
        'M-80 270 C 180 170, 300 400, 560 300 S 820 220, 900 350',
        'M-80 360 C 200 260, 340 470, 580 380 S 830 320, 900 440',
    ].map((d, i) => `<path d="${d}" stroke="${STOPS[i]}"/>`).join('');
    return `<svg class="oq-feature-guide__warp" viewBox="0 0 800 400" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">`
        + `<defs><linearGradient id="${rainbow}" x1="0" y1="0" x2="1" y2="1">${stops}</linearGradient>`
        + `<filter id="${warp}" x="-20%" y="-20%" width="140%" height="140%"><feTurbulence type="fractalNoise" baseFrequency=".0045 .009" numOctaves="2" seed="7"/><feDisplacementMap in="SourceGraphic" scale="190" xChannelSelector="R" yChannelSelector="G"/><feGaussianBlur stdDeviation="10"/></filter>`
        + `<filter id="${grain}" x="0" y="0" width="100%" height="100%"><feTurbulence type="fractalNoise" baseFrequency=".55" numOctaves="2" stitchTiles="stitch"/><feColorMatrix type="saturate" values="0"/><feComponentTransfer><feFuncA type="linear" slope="0.7"/></feComponentTransfer></filter></defs>`
        + `<rect width="800" height="400" fill="url(#${rainbow})"/>`
        + `<g filter="url(#${warp})" fill="none" stroke-width="44" stroke-linecap="round">${bands}</g>`
        + `<g filter="url(#${warp})" fill="none" stroke="white" stroke-opacity="0.28" stroke-width="10" stroke-linecap="round"><path d="M-80 135 C 150 40, 310 270, 530 165 S 820 80, 900 210"/><path d="M-80 315 C 190 215, 320 430, 570 340 S 825 270, 900 395"/></g>`
        + `<rect width="800" height="400" filter="url(#${grain})" opacity="0.7" style="mix-blend-mode:overlay"/></svg>`;
}
//# sourceMappingURL=featureIllustrations.js.map